<script lang="ts" setup>
import type { CSSProperties } from 'vue';
import type { CropSelection } from '../../runtime/cropper';
import CropperRange from './CropperRange.vue';

defineProps<{
    loading: boolean;
    controlsTarget?: string;
    editorFileName: string;
    originalWidth: number;
    originalHeight: number;
    canvasDisplayStyle: CSSProperties;
    cropSelectionStyle: CSSProperties;
    cropSelection: CropSelection;
}>();

const emit = defineEmits<{
    (e: 'set-viewport-zoom', value: number): void;
    (e: 'nudge-selection', dx: number, dy: number, resize: boolean): void;
    (e: 'start-crop', event: MouseEvent): void;
    (e: 'start-move', event: MouseEvent): void;
    (e: 'start-resize', handle: string, event: MouseEvent): void;
}>();

const { $t } = useLocalization();

const viewportZoom = defineModel<number>('viewportZoom', { required: true });
const canvas = ref<HTMLCanvasElement | null>(null);
const viewport = ref<HTMLDivElement | null>(null);
const panMode = ref(false);
const panning = ref(false);
let stopPan: (() => void) | null = null;
onBeforeUnmount(() => stopPan?.());

defineExpose({
    canvas,
    viewport
});

function startPan(event: MouseEvent)
{
    if (!panMode.value && event.button !== 1)
        return false;

    event.preventDefault();
    event.stopPropagation();

    stopPan?.();
    panning.value = true;

    const startX = event.clientX;
    const startY = event.clientY;
    const scrollLeft = viewport.value.scrollLeft;
    const scrollTop = viewport.value.scrollTop;

    function move(moveEvent: MouseEvent)
    {
        viewport.value.scrollLeft = scrollLeft - (moveEvent.clientX - startX);
        viewport.value.scrollTop = scrollTop - (moveEvent.clientY - startY);
    }

    function stop()
    {
        panning.value = false;
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', stop);
        stopPan = null;
    }

    stopPan = stop;
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);

    return true;
}

function onCanvasMouseDown(event: MouseEvent)
{
    if (startPan(event))
        return;

    emit('start-crop', event);
}

function onSelectionMouseDown(event: MouseEvent)
{
    if (startPan(event))
        return;

    emit('start-move', event);
}

function onResizeMouseDown(handle: string, event: MouseEvent)
{
    if (startPan(event))
        return;

    emit('start-resize', handle, event);
}
function onSelectionKeydown(event: KeyboardEvent)
{
    const directions: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const direction = directions[event.key];
    if (!direction) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 10 : 1;
    emit('nudge-selection', direction[0] * step, direction[1] * step, event.altKey);
}
</script>

<template>
    <div class="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[8px] border border-border bg-app-bg max-xl:min-h-[360px] dark:border-border-dark dark:bg-app-bg-dark">
        <div ref="viewport" class="editor-canvas-wrap relative flex min-h-0 min-w-0 flex-1 overflow-auto p-[20px] max-lg:min-h-[280px]" :class="{ 'select-none': panning }">
            <div v-if="loading" class="absolute inset-0 flex items-center justify-center text-fg-muted dark:text-fg-muted-dark">
                <IdeoLoadingState :label="$t('[[[Ładowanie obrazu...]]]')" />
            </div>
            <div v-else class="relative m-auto w-max shrink-0 overflow-hidden" :style="canvasDisplayStyle">
                <canvas ref="canvas" class="editor-canvas block bg-surface shadow-[0_10px_30px_-12px_rgb(0_0_0/0.3)]" :class="panning ? 'cursor-grabbing' : panMode ? 'cursor-grab' : 'cursor-crosshair'" :style="canvasDisplayStyle" @mousedown="onCanvasMouseDown" />
                <div v-if="cropSelection.active" tabindex="0" role="group" :aria-label="$t('[[[Kadr. Strzałki przesuwają, Alt i strzałki zmieniają rozmiar. Shift zwiększa krok.]]]')" @keydown="onSelectionKeydown" class="editor-selection absolute border-[2px] border-accent bg-accent/10 shadow-[0_0_0_9999px_rgb(0_0_0_/_32%)] outline outline-2 outline-white" :class="panning ? 'cursor-grabbing' : panMode ? 'cursor-grab' : 'cursor-move'" :style="cropSelectionStyle" @mousedown="onSelectionMouseDown">
                    <span class="resize-handle absolute size-[14px] rounded-[3px] border-[2px] border-white bg-accent ring-1 ring-black/30 -start-[7px] -top-[7px] cursor-nw-resize" @mousedown="onResizeMouseDown('nw', $event)" />
                    <span class="resize-handle absolute size-[14px] rounded-[3px] border-[2px] border-white bg-accent ring-1 ring-black/30 -end-[7px] -top-[7px] cursor-ne-resize" @mousedown="onResizeMouseDown('ne', $event)" />
                    <span class="resize-handle absolute size-[14px] rounded-[3px] border-[2px] border-white bg-accent ring-1 ring-black/30 -start-[7px] -bottom-[7px] cursor-sw-resize" @mousedown="onResizeMouseDown('sw', $event)" />
                    <span class="resize-handle absolute size-[14px] rounded-[3px] border-[2px] border-white bg-accent ring-1 ring-black/30 -end-[7px] -bottom-[7px] cursor-se-resize" @mousedown="onResizeMouseDown('se', $event)" />
                    <span class="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded-[8px] bg-gray-900/90 px-2 py-0.5 text-ui-helper font-mono font-[600] text-white">{{ cropSelection.width }}×{{ cropSelection.height }}px</span>
                </div>
            </div>
        </div>
        <Portal :to="controlsTarget || 'cropper-zoom'" :disabled="!controlsTarget">
        <div class="flex flex-wrap items-center gap-[12px] text-fg-muted dark:text-fg-muted-dark">
            <span class="whitespace-nowrap text-ui-meta tabular-nums">{{ originalWidth }} × {{ originalHeight }} px</span>
            <div class="grid grid-cols-[30px_minmax(64px,100px)_30px_30px_36px] items-center gap-[6px]">
                <IdeoAction stop-propagation
                    variant="secondary"
                    icon="minus"
                    :disabled="loading || viewportZoom <= 25"
                    :title="$t('[[[Pomniejsz]]]')"
                    @click="emit('set-viewport-zoom', viewportZoom - 25)"
                />
                <CropperRange
                    v-model="viewportZoom"
                    class="min-w-0"
                    :aria-label="$t('[[[Powiększenie obrazu]]]')"
                    :disabled="loading"
                    :min="25"
                    :max="300"
                    :step="25"
                />
                <IdeoAction stop-propagation
                    variant="secondary"
                    icon="plus"
                    :disabled="loading || viewportZoom >= 300"
                    :title="$t('[[[Powiększ]]]')"
                    @click="emit('set-viewport-zoom', viewportZoom + 25)"
                />
                <IdeoAction stop-propagation
                    variant="secondary"
                    :class="panMode ? '!bg-border dark:!bg-border-strong-dark' : ''"
                    icon="hand"
                    :title="$t('[[[Przesuwanie widoku]]]')"
                    :aria-pressed="panMode"
                    @click="panMode = !panMode"
                />
                <span class="text-start text-[12px] tabular-nums">{{ viewportZoom }}%</span>
            </div>
        </div>

        </Portal>
    </div>
</template>
